import { useState, useRef } from 'react';
import { Formik, Form } from "formik";
import {
    Col,
    Container,
    Row,
    Button,
    Card,
    Spinner,
} from 'react-bootstrap';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import axios from '../../../../apis/Api';
import FormikControl from "../../../../layouts/Input/FormikControl";
import {
    AllowOnlyAlphaNumericWithDash,
    AllowOnlyNumeric
} from '../../../../utilities/Validations';
import RepaymentConfirmationModal from './RepaymentConfirmationModal';

const initialValues = {
    loanDisplayId: '',
    amount: '',
    paymentDate: new Date(),
    tid: '',
};

const validationSchema = Yup.object({
    loanDisplayId: Yup.string().min(8).max(10).required(),
    amount: Yup.number().typeError('Amount must be numeric').positive().required(),
    paymentDate: Yup.date().max(new Date()).required(),
    tid: Yup.string().matches(/^\d+$/).length(12).required(),
});

const ApplyLoanRepaymentForm = ({ onClose }) => {
    const formikRef = useRef(null);

    const [isSubmittingExtra, setIsSubmittingExtra] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [confirmationData, setConfirmationData] = useState(null);

    const handleSubmit = async (values, { setSubmitting }) => {
        setIsSubmittingExtra(true);
        const d = new Date(values.paymentDate);
        const payload = {
            loanDisplayId: values.loanDisplayId.trim(),
            amount: Number(values.amount),
            paymentDate: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
            tid: values.tid.trim(),
        };

        try {
            const { data } = await axios.post(
                '/api/Lender/CreditBook/LoanRepaymentForWeb',
                payload
            );

            if (data?.isSuccess) {
                setConfirmationData(data.response);
                setShowConfirmationModal(true);
            } else {
                toast.error(data?.message || 'Submission failed');
            }
        } catch {
            toast.error('An error occurred');
        } finally {
            setSubmitting(false);
            setIsSubmittingExtra(false);
        }
    };

    const handleModalClose = () => {
        setShowConfirmationModal(false);
    };

    const handleModalSuccess = () => {
        setShowConfirmationModal(false);
        formikRef.current?.resetForm();
    };

    return (
        <>
            <Formik
                innerRef={formikRef}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {(formik) => (
                    <Form autoComplete="off">
                        <Container>
                            <Card
                                className="shadow-sm border-0"
                                style={{
                                    height: 'fit-content',
                                    minHeight: 'unset',
                                    flex: 'none'
                                }}
                            >
                                <Card.Body>
                                    <p className="text-muted">
                                        Enter repayment details carefully before submission
                                    </p>

                                    <Row className="g-3">
                                        <Col md={6}>
                                            <FormikControl
                                                formik={formik}
                                                control="input"
                                                type="text"
                                                label="Loan Display ID"
                                                name="loanDisplayId"
                                                value={formik.values["loanDisplayId"] ?? ''}
                                                maxLength={10}
                                                handleOnChange={(val) =>
                                                    formik.setFieldValue(
                                                        'loanDisplayId',
                                                        AllowOnlyAlphaNumericWithDash(val)
                                                    )
                                                }
                                            />
                                        </Col>

                                        <Col md={6}>
                                            <FormikControl
                                                formik={formik}
                                                control="input"
                                                type="text"
                                                label="Transaction ID (TID)"
                                                name="tid"
                                                value={formik.values["tid"] ?? ''}
                                                maxLength={12}
                                                handleOnChange={(val) =>
                                                    formik.setFieldValue(
                                                        'tid',
                                                        AllowOnlyNumeric(val)
                                                    )
                                                }
                                            />
                                        </Col>

                                        <Col md={6}>
                                            <FormikControl
                                                formik={formik}
                                                control="input"
                                                type="number"
                                                label="Amount"
                                                name="amount"
                                                value={formik.values["amount"] ?? ''}
                                                min={0}
                                                handleOnChange={(val) =>
                                                    formik.setFieldValue(
                                                        'amount',
                                                        AllowOnlyNumeric(val)
                                                    )
                                                }
                                            />
                                        </Col>

                                        <Col md={6}>
                                            <FormikControl
                                                formik={formik}
                                                control="date"
                                                label="Payment Date"
                                                name="paymentDate"
                                                value={formik.values.paymentDate}
                                                maxDate={new Date()}
                                                handleOnChange={(value) =>
                                                    formik.setFieldValue('paymentDate', value)
                                                }
                                            />
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                            <div className="d-flex justify-content-end gap-2 mt-4">
                                <Button
                                    variant="outline-secondary"
                                    onClick={onClose}
                                    disabled={formik.isSubmitting || isSubmittingExtra}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    disabled={
                                        !formik.isValid ||
                                        !formik.dirty ||
                                        formik.isSubmitting ||
                                        isSubmittingExtra
                                    }
                                >
                                    {isSubmittingExtra ? (
                                        <>
                                            <Spinner
                                                animation="border"
                                                size="sm"
                                                className="me-2"
                                            />
                                            Submitting...
                                        </>
                                    ) : (
                                        'Submit Repayment'
                                    )}
                                </Button>
                            </div>
                        </Container>
                    </Form>
                )}
            </Formik>

            <RepaymentConfirmationModal
                show={showConfirmationModal}
                handleClose={handleModalClose}
                onSuccess={handleModalSuccess}
                data={confirmationData}
            />
        </>
    );
};

export default ApplyLoanRepaymentForm;
